import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../../context/authContext";
import { ROLES, ROLE_LABELS } from "../../constants/roles";
import { useVentureDashboardHook } from "../../hooks/useVentureHooks";
import { getNavSectionsForRole } from "./navConfigs";
import "./sidebar.css";

// localStorage key so the collapsed state survives refreshes / route changes.
const COLLAPSE_STORAGE_KEY = "ih_sidebar_collapsed";

// Brand sub-text shown under "Imagine Her" in the sidebar header.
// Each role gets phrasing that matches their workspace.
const BRAND_SUB_BY_ROLE = {
  [ROLES.ADMIN]: "ASPIRE",
  [ROLES.MANAGER]: "ASPIRE",
  [ROLES.FUNDER]: "Funder Portal",
  [ROLES.VENTURE]: "Entrepreneur Portal",
};

/*
 * Sidebar
 * -------
 * Presentational sidebar shared by all role workspaces. The list of links is
 * driven by the user's role via `getNavSectionsForRole(role)`, so adding new
 * links per role only requires editing navConfigs.js.
 *
 * Collapsible: click the chevron in the header to toggle between an expanded
 * view (icons + labels) and a collapsed rail (icons only). The state is
 * persisted to localStorage so the user's preference sticks.
 */
export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Initialise from localStorage so the first paint matches the saved state.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true",
  );

  // Persist on every change.
  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate("/signin", { replace: true });
  };

  if (!user) return null;

  const sections = getNavSectionsForRole(user.role);
  const brandSub = BRAND_SUB_BY_ROLE[user.role] ?? "ASPIRE";

  // For venture users we show a small panel with their business info under the
  // brand block. Other roles don't have a single venture context, so this is
  // intentionally venture-only. Shares the React Query cache with the venture
  // dashboard so this doesn't trigger an extra network request.
  const isVentureUser = user.role === ROLES.VENTURE;
  const { data: ventureData } = useVentureDashboardHook({
    enabled: isVentureUser,
  });
  const venture = isVentureUser && ventureData ? ventureData : null;

  // Single class string lets CSS target the collapsed variant via `.sidebar-collapsed`.
  const asideClassName = "sidebar" + (collapsed ? " sidebar-collapsed" : "");

  return (
    <aside className={asideClassName} data-collapsed={collapsed}>
      {/* Brand block + collapse toggle */}
      <div className="sidebar-brand">
        <div className="sidebar-brand-mark">
          {/* <span>IH</span> */}
          <img
            src="/cropped-fav-icon.png"
            alt="Imagine Her logo"
            className="sidebar-brand-logo"
          />
        </div>
        {/* Brand text hides when collapsed (via CSS) so the rail is icon-only */}
        <div className="sidebar-brand-text">
          <p className="sidebar-brand-name">Imagine Her</p>
          <p className="sidebar-brand-sub">{brandSub}</p>
        </div>
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setCollapsed((collapsed) => !collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronRight className="sidebar-toggle-icon" aria-hidden="true" />
          ) : (
            <ChevronLeft className="sidebar-toggle-icon" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Venture-only context panel: business name + stage / status badges.
          Hidden in collapsed mode so the rail stays icon-only. */}
      {venture && (
        <div className="sidebar-venture">
          <p className="sidebar-venture-name">{venture.name}</p>
          <div className="sidebar-venture-badges">
            <span className="sidebar-badge sidebar-badge-solid">
              {venture.stage || "Unclassified"}
            </span>
            <span className="sidebar-badge sidebar-badge-outline">
              {venture.status || "active"}
            </span>
          </div>
        </div>
      )}

      {/* Scrollable nav area — sections render with a divider between them */}
      <nav className="sidebar-nav">
        {sections.map((items, sectionIdx) => (
          <div key={sectionIdx} className="sidebar-nav-section">
            {sectionIdx > 0 && <div className="sidebar-divider" />}
            {items.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                // When collapsed we still want the label to show as a native
                // tooltip on hover so users can identify icons.
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  "sidebar-link" + (isActive ? " sidebar-link-active" : "")
                }
              >
                <Icon className="sidebar-link-icon" aria-hidden="true" />
                <span className="sidebar-link-label">{label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer: who's signed in + sign-out button */}
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <p className="sidebar-user-name">{user.name}</p>
          <p className="sidebar-user-role">{ROLE_LABELS[user.role]}</p>
        </div>
        <button
          type="button"
          className="sidebar-logout"
          onClick={handleLogout}
          title={collapsed ? "Sign out" : undefined}
        >
          <LogOut className="sidebar-link-icon" aria-hidden="true" />
          <span className="sidebar-link-label">Sign out</span>
        </button>
      </div>
    </aside>
  );
}
